import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { selectHasFiber, selectPlanCharacteristics } from '@app/redux';
import { isFiberAidPlan, showTitleAddons } from '@app/utils';

import { GetPlanOffersPayload } from '@mfe/shared/schema-types';

import MarketingAttributes from '../../MarketingAttributes';
import PlanCard from '../../PlanCard';
import PlanCardBody from '../../PlanCardBody';
import PlanCardCTA from '../../PlanCardCTA';
import PlanCardPrice from '../../PlanCardPrice';
import TitleAddOns from '../../TitleAddOns';
import LifelineNotice from '../LifelineNotice';
import { DirectiveCharacteristics, FamilyPlan, RouterText, Variant } from '../../../types';

type AvailablePlanProps = {
  handleClick: () => void;
  plan: GetPlanOffersPayload;
  broadbandLabel?: ReactNode | null;
};

const AvailablePlan = ({ handleClick, broadbandLabel, plan }: AvailablePlanProps) => {
  const hasFiber = useSelector(selectHasFiber);
  const {
    characteristics: { id: currentPlanProductTypeId },
  } = useSelector(selectPlanCharacteristics);
  const { t } = useTranslation('ChangePlan');

  const { id, characteristics, priceCurrency, price } = plan;
  const {
    productFamily,
    titleAddOns,
    offerName,
    routerText,
    attributes,
    onCallDirective,
    onCallDirectiveClientMessage,
  } = characteristics;

  const hasFiberAidPlan = hasFiber && isFiberAidPlan(currentPlanProductTypeId);
  const isVoicePlan = routerText === RouterText.Voice;
  const hasOnCallDirective = onCallDirective === DirectiveCharacteristics.OnCallDirective && hasFiber;
  const callToOrder: boolean = isVoicePlan || hasFiberAidPlan || hasOnCallDirective;

  const showTitleAddOns = showTitleAddons(productFamily, offerName, titleAddOns);
  const isCafWithoutVoice = productFamily === FamilyPlan.CAFII && !isVoicePlan;

  const showMarketingAttributes = Array.isArray(attributes) && attributes.length;
  const callToOrderMessage =
    onCallDirectiveClientMessage ||
    t('plan.callToOrder.buttonLabel', {
      phoneNumber: t('plan.callToOrder.phoneNumber'),
    });

  if (!price || !priceCurrency) {
    return null;
  }
  return (
    <PlanCard name={offerName} variant={Variant.DEFAULT} broadbandLabel={broadbandLabel}>
      <PlanCardBody id={id} characteristics={characteristics} disclosure={null} isVoicePlan={isVoicePlan} />
      {showTitleAddOns && <TitleAddOns characteristic={titleAddOns || ''} />}
      <PlanCardCTA callToOrder={callToOrder} handleClick={handleClick} callToOrderMessage={callToOrderMessage} />
      {isCafWithoutVoice ? <LifelineNotice /> : null}
      <PlanCardPrice price={price.toString()} currency={priceCurrency} />
      {showMarketingAttributes ? <MarketingAttributes attributes={characteristics.attributes} /> : null}
    </PlanCard>
  );
};

export default AvailablePlan;
